import type { Survicate } from '@survicate/survicate-web-surveys-wrapper'
import {
  getSurvicateInstance,
  initSurvicate,
} from '@survicate/survicate-web-surveys-wrapper/widget_wrapper'
import { makeAutoObservable, runInAction } from 'mobx'

import config from '@src/config'
import Deferred from '@src/lib/Deferred'
import type { UserModel } from '@src/service/model'

export default class SurveyService {
  private survicate: Survicate | null = null
  private survicateInitialization = new Deferred<void>()

  constructor() {
    makeAutoObservable(this)

    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    this.initSurvicate()
  }

  private async initSurvicate() {
    if (this.survicateInitialization.state === 'fulfilled') {
      return
    }

    try {
      await initSurvicate({
        workspaceKey: config.SURVICATE_WORKSPACE_KEY,
        // Since we have our own logic for determining when to show a survey,
        // prevent a survey from appearing immediately after loading the page.
        // See "Disable automatic targeting" on the docs:
        // https://developers.survicate.com/javascript/methods/
        disableTargeting: true,
      })

      runInAction(() => {
        this.survicate = getSurvicateInstance()
      })

      this.survicateInitialization.resolve()
    } catch (error) {
      this.survicateInitialization.reject(error)
    }
  }

  async showSurvey(
    surveyId: Parameters<Survicate['showSurvey']>[0],
    options?: Parameters<Survicate['showSurvey']>[1],
  ): Promise<ReturnType<Survicate['showSurvey']>> {
    if (!surveyId || this.survicateInitialization.state === 'rejected') {
      return false
    }

    await this.survicateInitialization.promise

    if (!this.survicate) {
      return false
    }

    return this.survicate.showSurvey(surveyId, options)
  }

  async identify(user: UserModel) {
    if (this.survicateInitialization.state === 'rejected') {
      return
    }

    await this.survicateInitialization.promise

    if (!this.survicate) {
      return
    }

    this.survicate.setVisitorTraits({
      userId: user.id,
    })
  }

  tearDown() {
    // noop
  }
}
